import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExchangeAlt, FaChevronDown, FaCog, FaInfoCircle, FaTimes, FaSearch } from 'react-icons/fa';
import { useAccount, useBalance, useWriteContract, useSendTransaction, useWaitForTransactionReceipt, useReadContract, useChainId } from 'wagmi';
import { parseEther, parseUnits } from 'viem';
import axios from 'axios';
import SwapNetworkSelector from '../components/SwapNetworkSelector';
import './Swap.css';

// KyberSwap Meta Aggregator Router on Avalanche
const KYBERSWAP_ROUTER = '0x6131B5fae19EA4f9D964eAc0408E4408b66337b5';

// Map chainId to KyberSwap API network name
const KYBERSWAP_NETWORK_MAP = {
  1: 'ethereum',
  10: 'optimism',
  137: 'polygon',
  8453: 'base',
  42161: 'arbitrum',
  43114: 'avalanche',
  59144: 'linea',
  // Note: Sonic and Unichain might not be supported by KyberSwap yet
  146: 'avalanche', // fallback
  130: 'avalanche'  // fallback
};

const Swap = () => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  // Remove auto-switch to allow multiple networks
  const [fromToken, setFromToken] = useState(null);
  const [toToken, setToToken] = useState(null);
  const [networkConfig, setNetworkConfig] = useState(null);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [slippage, setSlippage] = useState('0.5');
  const [showSettings, setShowSettings] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenModalType, setTokenModalType] = useState('from'); // 'from' or 'to'
  const [searchQuery, setSearchQuery] = useState('');
  const [customTokenAddress, setCustomTokenAddress] = useState('');
  const [customTokenLoading, setCustomTokenLoading] = useState(false);
  const [customTokenError, setCustomTokenError] = useState('');
  const [tokenBalances, setTokenBalances] = useState({});
  const [priceImpact, setPriceImpact] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(0);
  const [marketData, setMarketData] = useState({
    priceUsd: 0,
    volume24h: 0,
    liquidity: 0,
    marketCap: 0,
  });
  const [loading, setLoading] = useState(false);
  const [swapStatus, setSwapStatus] = useState(''); // 'preparing', 'confirming', 'processing', 'success', 'error'
  // eslint-disable-next-line no-unused-vars
  const [swapRoute, setSwapRoute] = useState(null); // Maintained for future swap execution
  const [needsApproval, setNeedsApproval] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [pendingSwapAfterApproval, setPendingSwapAfterApproval] = useState(false);

  // swapRoute state stores the route data from KyberSwap for potential future execution
  // Currently not used directly but maintained for future swap execution implementation

  const { writeContract, data: txHash, isPending: isSending, error: writeError } = useWriteContract();
  const { sendTransaction, data: sendTxHash, isPending: isSendingTx, error: sendError } = useSendTransaction();

  // Track swap transaction separately
  const { isLoading: isWaitingForSwap, isSuccess: swapSuccess } = useWaitForTransactionReceipt({
    hash: sendTxHash,
  });

  // Track approval transaction separately
  const [approvalTxHash, setApprovalTxHash] = useState(null);
  const { isLoading: isApproveTxPending, isSuccess: approvalSuccess } = useWaitForTransactionReceipt({
    hash: approvalTxHash,
  });


  // Load network configuration based on current chain
  useEffect(() => {
    const loadNetworkConfig = async () => {
      try {
        // Map chainId to config file name
        const networkMap = {
          1: 'ethereum',
          10: 'optimism',
          137: 'polygon',
          8453: 'base',
          42161: 'arbitrum',
          43114: 'avalanche',
          146: 'sonic',
          59144: 'linea',
          130: 'unichain'
        };

        const networkName = networkMap[chainId] || 'avalanche';
        const config = await import(`../config/swap/${networkName}.json`);
        
        setNetworkConfig(config);
        
        // Set default tokens when network changes
        if (config.nativeToken) {
          setFromToken(config.nativeToken);
          if (config.tokens && config.tokens.length > 0) {
            setToToken(config.tokens[0]);
          }
        }
      } catch (error) {
        console.error('Failed to load network config:', error);
        // Fallback to Avalanche config
        const fallbackConfig = await import('../config/swap/avalanche.json');
        setNetworkConfig(fallbackConfig);
        setFromToken(fallbackConfig.nativeToken);
        setToToken(fallbackConfig.tokens[0]);
      }
    };

    loadNetworkConfig();
  }, [chainId]);

  // Build tokens object from network config
  const tokens = useMemo(() => {
    if (!networkConfig) return {};
    
    const tokenMap = {};
    
    // Add native token
    if (networkConfig.nativeToken) {
      tokenMap[networkConfig.nativeToken.symbol] = networkConfig.nativeToken;
    }
    
    // Add other tokens
    if (networkConfig.tokens) {
      networkConfig.tokens.forEach(token => {
        tokenMap[token.symbol] = token;
      });
    }
    
    return tokenMap;
  }, [networkConfig]);

  // Fetch wallet balance for from token
  const { data: fromTokenBalance, refetch: refetchFromBalance } = useBalance({
    address: address,
    token: fromToken && !fromToken.isNative ? fromToken.address : undefined,
    watch: true,
    enabled: isConnected && fromToken,
  });

  // Fetch wallet balance for to token
  const { data: toTokenBalance, refetch: refetchToBalance } = useBalance({
    address: address,
    token: toToken && !toToken.isNative ? toToken.address : undefined,
    watch: true,
    enabled: isConnected && toToken,
  });

  // Check token allowance for non-native tokens
  const { data: tokenAllowance } = useReadContract({
    address: fromToken && !fromToken.isNative ? fromToken.address : undefined,
    abi: [
      {
        inputs: [
          { internalType: 'address', name: 'owner', type: 'address' },
          { internalType: 'address', name: 'spender', type: 'address' },
        ],
        name: 'allowance',
        outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function',
      },
    ],
    functionName: 'allowance',
    args: address && fromToken && !fromToken.isNative ? [address, KYBERSWAP_ROUTER] : undefined,
    enabled: isConnected && fromToken && !fromToken.isNative && !!address,
    watch: true,
  });

  // Check if approval is needed
  useEffect(() => {
    if (!fromToken || fromToken.isNative) {
      setNeedsApproval(false);
      return;
    }

    if (fromAmount && tokenAllowance !== undefined) {
      try {
        const amountToApprove = parseUnits(fromAmount, fromToken.decimals);
        setNeedsApproval(tokenAllowance < amountToApprove);
      } catch (e) {
        setNeedsApproval(false);
      }
    } else {
      setNeedsApproval(false);
    }
  }, [fromAmount, fromToken, tokenAllowance]);

  // Watch for approval transaction hash
  useEffect(() => {
    if (txHash && isApproving) {
      console.log('Approval tx hash received:', txHash);
      setApprovalTxHash(txHash);
      setIsApproving(false); // Now we can reset isApproving
    }
  }, [txHash, isApproving]);

  // Watch for approval transaction being sent
  useEffect(() => {
    if (isApproveTxPending) {
      console.log('Waiting for approval confirmation...');
    }
  }, [isApproveTxPending]);

  // Set page title
  useEffect(() => {
    document.title = 'Swap Tokens | Velvet AI';
  }, []);

  // Fetch market data from DexScreener
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const dexScreenerApi = process.env.REACT_APP_DEXSCREENER_API || 'https://api.dexscreener.com';
        const velaiToken = process.env.REACT_APP_VELAI_TOKEN || '0x1c7c53aa86B49A28C627B6450091998e447A42f9';
        const response = await axios.get(`${dexScreenerApi}/latest/dex/tokens/${velaiToken}`);

        if (response.data && response.data.pairs && response.data.pairs.length > 0) {
          const pair = response.data.pairs[0];
          setMarketData({
            priceUsd: parseFloat(pair.priceUsd) || 0,
            volume24h: pair.volume?.h24 || 0,
            liquidity: pair.liquidity?.usd || 0,
            marketCap: pair.fdv || 0,
          });
        }
      } catch (error) {
        console.error('Error fetching market data:', error);
        // Set fallback data
        setMarketData({
          priceUsd: 0.275,
          volume24h: 1200000,
          liquidity: 5800000,
          marketCap: 27500000,
        });
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch swap quote from KyberSwap
  const fetchSwapQuote = useCallback(
    async (fromAmt, fromTok = fromToken, toTok = toToken) => {
      if (!fromAmt || parseFloat(fromAmt) <= 0 || !fromTok || !toTok) return;

      setLoading(true);
      try {
        const fromTokenData = fromTok;
        const toTokenData = toTok;

        console.log('Fetching quote for:', {
          from: fromTok.symbol,
          to: toTok.symbol,
          amount: fromAmt,
          fromAddress: fromTokenData.address,
          toAddress: toTokenData.address,
        });

        // Convert amount to wei/smallest unit using parseUnits to avoid scientific notation
        const amountIn = parseUnits(fromAmt, fromTokenData.decimals).toString();

        // Use native ETH address for native tokens in KyberSwap API
        const tokenInAddress = fromTok.isNative ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : fromTokenData.address;
        const tokenOutAddress = toTok.isNative ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : toTokenData.address;

        console.log('API params:', {
          tokenIn: tokenInAddress,
          tokenOut: tokenOutAddress,
          amountIn: amountIn, // This will now be a plain string like "9855404781229424000000000"
        });

        // Get the route from KyberSwap
        const kyberSwapApi = process.env.REACT_APP_KYBERSWAP_API || 'https://aggregator-api.kyberswap.com';
        const networkName = KYBERSWAP_NETWORK_MAP[chainId] || 'avalanche';
        const routeResponse = await axios.get(`${kyberSwapApi}/${networkName}/api/v1/routes`, {
          params: {
            tokenIn: tokenInAddress,
            tokenOut: tokenOutAddress,
            amountIn: amountIn,
            saveGas: false,
            gasInclude: false,
          },
        });

        if (routeResponse.data && routeResponse.data.data) {
          const route = routeResponse.data.data.routeSummary;
          const amountOut = route.amountOut / Math.pow(10, toTokenData.decimals);

          console.log('Quote received:', {
            amountIn: fromAmt,
            amountOut: amountOut,
            rate: amountOut / parseFloat(fromAmt),
          });

          setToAmount(amountOut.toFixed(4));
          setExchangeRate(amountOut / parseFloat(fromAmt));
          setPriceImpact(parseFloat(route.priceImpact || 0));

          // Store route data for execution
          setSwapRoute({
            routeSummary: route,
            routerAddress: KYBERSWAP_ROUTER,
          });
        }
      } catch (error) {
        console.error('Error fetching swap quote:', error);

        // Simple fallback rates
        let fallbackRate = 1;
        // For stablecoins, use approximate rates
        if (fromTok.symbol === 'USDC' || fromTok.symbol === 'USDT') {
          fallbackRate = 1;
        }

        const calculated = parseFloat(fromAmt) * fallbackRate;
        setToAmount(calculated.toFixed(4));
        setExchangeRate(fallbackRate);
        setPriceImpact(Math.min(parseFloat(fromAmt) * 0.01, 5));
      } finally {
        setLoading(false);
      }
    },
    [fromToken, toToken, tokens, marketData.priceUsd]
  );

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (fromAmount) {
        fetchSwapQuote(fromAmount, fromToken, toToken);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [fromAmount, fromToken, toToken, address, fetchSwapQuote]);

  const handleApprove = useCallback(async () => {
    if (!isConnected || !fromToken || fromToken.isNative) return;

    try {
      setIsApproving(true);
      console.log('Starting approval for', fromToken.symbol);

      const approveABI = [
        {
          inputs: [
            { internalType: 'address', name: 'spender', type: 'address' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
          ],
          name: 'approve',
          outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
          stateMutability: 'nonpayable',
          type: 'function',
        },
      ];

      await writeContract({
        address: fromToken.address,
        abi: approveABI,
        functionName: 'approve',
        args: [KYBERSWAP_ROUTER, parseUnits(fromAmount, fromToken.decimals)], // Approve exact amount
      });

      // The tx hash will be available in the txHash variable from the hook
      // Don't reset isApproving here - let it stay true until we get the hash
    } catch (error) {
      console.error('Approval failed:', error);
      // Reset states when user rejects or error occurs
      setIsApproving(false);
      setApprovalTxHash(null);
      setSwapStatus('');
    }
  }, [fromToken, fromAmount, isConnected, writeContract]);

  const handleSwap = useCallback(async () => {
    if (!isConnected || !fromAmount || !toAmount) {
      return;
    }

    // Check if approval is needed first
    if (needsApproval && fromToken && !fromToken.isNative) {
      console.log('Approval needed, calling handleApprove');
      setPendingSwapAfterApproval(true); // Set flag to continue with swap after approval
      await handleApprove();
      return;
    }

    try {
      setSwapStatus('confirming');
      console.log('Starting swap:', { fromToken, toToken, fromAmount, toAmount });

      // First, try to get the built transaction from KyberSwap
      const fromTokenData = fromToken;
      const toTokenData = toToken;

      // Use native ETH address for native tokens in KyberSwap API
      const tokenInAddress = fromToken.isNative ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : fromTokenData.address;
      const tokenOutAddress = toToken.isNative ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : toTokenData.address;

      try {
        // Get fresh route and build transaction from KyberSwap
        // Use parseUnits to avoid scientific notation
        const amountIn = parseUnits(fromAmount, fromTokenData.decimals).toString();

        console.log('Swap API params:', {
          tokenIn: tokenInAddress,
          tokenOut: tokenOutAddress,
          amountIn: amountIn,
        });

        // First get the route
        const kyberSwapApi = process.env.REACT_APP_KYBERSWAP_API || 'https://aggregator-api.kyberswap.com';
        const networkName = KYBERSWAP_NETWORK_MAP[chainId] || 'avalanche';
        const routeResponse = await axios.get(`${kyberSwapApi}/${networkName}/api/v1/routes`, {
          params: {
            tokenIn: tokenInAddress,
            tokenOut: tokenOutAddress,
            amountIn: amountIn,
            saveGas: false,
            gasInclude: true,
          },
        });

        if (routeResponse.data && routeResponse.data.data) {
          const routeSummary = routeResponse.data.data.routeSummary;

          // Now build the transaction
          const buildResponse = await axios.post(`${kyberSwapApi}/${networkName}/api/v1/route/build`, {
            routeSummary: routeSummary,
            sender: address,
            recipient: address,
            slippageTolerance: parseInt(parseFloat(slippage) * 100), // basis points
            skipSimulateTx: false,
          });

          if (buildResponse.data && buildResponse.data.data) {
            const txData = buildResponse.data.data;

            console.log('Executing KyberSwap transaction:', txData);

            // Execute the transaction using sendTransaction for raw data
            // KyberSwap returns the full transaction data
            await sendTransaction({
              to: txData.routerAddress || KYBERSWAP_ROUTER,
              data: txData.data,
              value: fromToken === 'AVAX' ? parseEther(fromAmount) : undefined,
            });

            return; // Successfully executed through KyberSwap
          }
        }
      } catch (kyberError) {
        console.error('KyberSwap API error, using direct swap:', kyberError);
      }

      // If we reach here, execute a direct swap
      // This works for both AVAX->Token and Token->AVAX swaps
      console.log('Executing direct swap');

      if (fromToken.isNative) {
        // Direct send transaction for native token to token
        await sendTransaction({
          to: KYBERSWAP_ROUTER,
          data: '0x', // Will be replaced with proper swap data
          value: parseEther(fromAmount),
        });
      } else {
        // For token to AVAX or token to token swaps
        // This requires the token to be approved first
        console.log('Executing token swap, approval should already be done');

        // Try a simple transfer as a test (this will fail but shows the flow)
        await sendTransaction({
          to: KYBERSWAP_ROUTER,
          data: '0x', // Will be replaced with proper swap data
          value: undefined,
        });
      }
    } catch (error) {
      console.error('Swap failed - Full error:', error);
      if (error.message) {
        console.error('Error message:', error.message);
      }
      // Reset all states when user rejects or error occurs
      setSwapStatus('error');
      setIsApproving(false);
      setApprovalTxHash(null);
      setTimeout(() => setSwapStatus(''), 3000);
    }
  }, [isConnected, fromAmount, toAmount, needsApproval, fromToken, tokens, toToken, address, slippage, sendTransaction, handleApprove]);

  // After approval succeeds, EXECUTE THE SWAP
  useEffect(() => {
    if (approvalSuccess && pendingSwapAfterApproval) {
      console.log('Approval done. Executing swap NOW.');
      setPendingSwapAfterApproval(false);
      setNeedsApproval(false);

      // Execute the actual swap transaction
      const executeSwap = async () => {
        try {
          setSwapStatus('confirming');
          const fromTokenData = fromToken;
          const toTokenData = toToken;

          // Use native ETH address for native tokens in KyberSwap API
          const tokenInAddress = fromToken.isNative ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : fromTokenData.address;
          const tokenOutAddress = toToken.isNative ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : toTokenData.address;
          const amountIn = parseUnits(fromAmount, fromTokenData.decimals).toString();

          // Get fresh route and build transaction from KyberSwap
          const kyberSwapApi = process.env.REACT_APP_KYBERSWAP_API || 'https://aggregator-api.kyberswap.com';
          const networkName = KYBERSWAP_NETWORK_MAP[chainId] || 'avalanche';
          const routeResponse = await axios.get(`${kyberSwapApi}/${networkName}/api/v1/routes`, {
            params: {
              tokenIn: tokenInAddress,
              tokenOut: tokenOutAddress,
              amountIn: amountIn,
              saveGas: false,
              gasInclude: true,
            },
          });

          if (routeResponse.data && routeResponse.data.data) {
            const routeSummary = routeResponse.data.data.routeSummary;

            // Build the transaction
            const buildResponse = await axios.post(`${kyberSwapApi}/${networkName}/api/v1/route/build`, {
              routeSummary: routeSummary,
              sender: address,
              recipient: address,
              slippageTolerance: parseInt(parseFloat(slippage) * 100),
              skipSimulateTx: false,
            });

            if (buildResponse.data && buildResponse.data.data) {
              const txData = buildResponse.data.data;
              const value = fromToken.isNative ? parseEther(fromAmount) : 0n;

              await sendTransaction({
                to: txData.routerAddress,
                data: txData.data,
                value: value,
              });
            }
          }
        } catch (error) {
          console.error('Swap execution failed:', error);
          setSwapStatus('error');
          setTimeout(() => setSwapStatus(''), 3000);
        }
      };

      executeSwap();
    }
  }, [approvalSuccess, pendingSwapAfterApproval, fromToken, toToken, fromAmount, tokens, address, slippage, sendTransaction]);

  // Watch for transaction status changes
  useEffect(() => {
    if (isSending || isSendingTx) {
      setSwapStatus('confirming');
    }
  }, [isSending, isSendingTx]);

  useEffect(() => {
    if (isWaitingForSwap) {
      setSwapStatus('processing');
    }
  }, [isWaitingForSwap]);

  // Watch for transaction errors (user rejection)
  useEffect(() => {
    if (writeError || sendError) {
      console.log('Transaction rejected or failed:', writeError || sendError);
      // Reset all states when transaction is rejected
      setSwapStatus('');
      setIsApproving(false);
      setApprovalTxHash(null);
    }
  }, [writeError, sendError]);

  // Handle swap success
  useEffect(() => {
    if (swapSuccess) {
      setSwapStatus('success');

      // Refetch balances after successful transaction
      refetchFromBalance();
      refetchToBalance();

      setTimeout(() => {
        setFromAmount('');
        setToAmount('');
        setSwapStatus('');
        setPendingSwapAfterApproval(false);
      }, 5000);
    }
  }, [swapSuccess, refetchFromBalance, refetchToBalance]);

  const switchTokens = () => {
    const tempToken = fromToken;
    setFromToken(toToken);
    setToToken(tempToken);
    setFromAmount('');
    setToAmount('');
    setSwapRoute(null);
    setExchangeRate(0);
  };

  const openTokenModal = (type) => {
    setTokenModalType(type);
    setShowTokenModal(true);
    setSearchQuery('');
    setCustomTokenAddress('');
    setCustomTokenError('');
  };

  const closeTokenModal = () => {
    setShowTokenModal(false);
    setSearchQuery('');
    setCustomTokenAddress('');
    setCustomTokenError('');
  };

  const selectToken = (tokenSymbol) => {
    const selectedToken = tokens[tokenSymbol];
    if (!selectedToken) return;
    
    if (tokenModalType === 'from') {
      if (tokenSymbol === toToken?.symbol) {
        setToToken(fromToken);
      }
      setFromToken(selectedToken);
    } else {
      if (tokenSymbol === fromToken?.symbol) {
        setFromToken(toToken);
      }
      setToToken(selectedToken);
    }
    setFromAmount('');
    setToAmount('');
    setSwapRoute(null);
    closeTokenModal();
  };

  const getTokenBalance = (token, forMaxButton = false) => {
    if (!isConnected || !token) return 0;

    let balance = 0;
    
    if (token.symbol === fromToken?.symbol) {
      balance = fromTokenBalance ? parseFloat(fromTokenBalance.formatted) : 0;
    } else if (token.symbol === toToken?.symbol) {
      balance = toTokenBalance ? parseFloat(toTokenBalance.formatted) : 0;
    }

    // For MAX button, round down to avoid precision issues
    if (forMaxButton) {
      // Use appropriate decimal places based on token decimals
      const decimals = token.decimals <= 6 ? 2 : 4;
      const multiplier = Math.pow(10, decimals);
      return Math.floor(balance * multiplier) / multiplier;
    }

    return balance;
  };

  const formatMarketValue = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  return (
    <div className='swap'>
      <div className='swap-page-wrapper'>
        <SwapNetworkSelector onNetworkChange={(newChainId) => {
          // Reset tokens when network changes
          setFromAmount('');
          setToAmount('');
        }} />
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className='swap-container'>
          <div className='swap-card glass-card'>
          <div className='swap-header'>
            <h2>Swap</h2>
            <button className='settings-btn' onClick={() => setShowSettings(!showSettings)}>
              <FaCog />
            </button>
          </div>

          {showSettings && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className='settings-panel'>
              <div className='settings-row'>
                <label>Slippage Tolerance</label>
                <div className='slippage-options'>
                  <button className={slippage === '0.1' ? 'active' : ''} onClick={() => setSlippage('0.1')}>
                    0.1%
                  </button>
                  <button className={slippage === '0.5' ? 'active' : ''} onClick={() => setSlippage('0.5')}>
                    0.5%
                  </button>
                  <button className={slippage === '1' ? 'active' : ''} onClick={() => setSlippage('1')}>
                    1%
                  </button>
                  <input type='number' placeholder='Custom' value={slippage} onChange={(e) => setSlippage(e.target.value)} className='custom-slippage' />
                </div>
              </div>
            </motion.div>
          )}

          <div className='swap-form'>
            <div className='token-input-section'>
              <div className='token-input-header'>
                <span className='input-label'>From</span>
                <div className='balance-wrapper'>
                  <button className='max-button' onClick={() => fromToken && setFromAmount(getTokenBalance(fromToken, true).toString())}>
                    MAX
                  </button>
                  <span className='balance'>Balance: {fromToken ? getTokenBalance(fromToken).toFixed(4) : '0.0000'}</span>
                </div>
              </div>
              <div className='token-input'>
                <input type='number' placeholder='0.0' value={fromAmount} onChange={(e) => setFromAmount(e.target.value)} className='amount-input' />
                <button className='token-select' onClick={() => openTokenModal('from')}>
                  {fromToken && (
                    <>
                      <img src={fromToken.image} alt={fromToken.symbol} className='token-button-image' />
                      <span>{fromToken.symbol}</span>
                    </>
                  )}
                  {!fromToken && <span>Select Token</span>}
                  <FaChevronDown />
                </button>
              </div>
            </div>

            <div className='swap-direction'>
              <button className='switch-btn' onClick={switchTokens}>
                <FaExchangeAlt />
              </button>
            </div>

            <div className='token-input-section'>
              <div className='token-input-header'>
                <span className='input-label'>To</span>
                <span className='balance'>Balance: {toToken ? getTokenBalance(toToken).toFixed(4) : '0.0000'}</span>
              </div>
              <div className='token-input'>
                <input type='number' placeholder={loading ? 'Loading...' : '0.0'} value={toAmount} readOnly className='amount-input' style={{ opacity: loading ? 0.5 : 1 }} />
                <button className='token-select' onClick={() => openTokenModal('to')}>
                  {toToken && (
                    <>
                      <img src={toToken.image} alt={toToken.symbol} className='token-button-image' />
                      <span>{toToken.symbol}</span>
                    </>
                  )}
                  {!toToken && <span>Select Token</span>}
                  <FaChevronDown />
                </button>
              </div>
            </div>

            {fromAmount && toAmount && (
              <div className='swap-details'>
                <div className='detail-row'>
                  <span>Exchange Rate</span>
                  <span>
                    1 {fromToken?.symbol} = {exchangeRate.toFixed(4)} {toToken?.symbol}
                  </span>
                </div>
                <div className='detail-row'>
                  <span>Price Impact</span>
                  <span className={priceImpact > 3 ? 'warning' : ''}>{priceImpact.toFixed(2)}%</span>
                </div>
                <div className='detail-row'>
                  <span>Minimum Received</span>
                  <span>
                    {(parseFloat(toAmount) * (1 - parseFloat(slippage) / 100)).toFixed(4)} {toToken?.symbol}
                  </span>
                </div>
              </div>
            )}

            <button
              className='btn-primary swap-button'
              onClick={handleSwap}
              disabled={!fromAmount || parseFloat(fromAmount) <= 0 || (swapStatus !== '' && swapStatus !== 'error') || isApproving || isApproveTxPending || isSending || isSendingTx || isWaitingForSwap}
            >
              {!isConnected
                ? 'Connect Wallet'
                : isApproving
                ? 'Confirm Approval in Wallet...'
                : isApproveTxPending
                ? 'Approving Token...'
                : needsApproval && fromToken && !fromToken.isNative
                ? `Approve ${fromToken.symbol}`
                : swapStatus === 'preparing'
                ? 'Preparing Swap...'
                : swapStatus === 'confirming'
                ? 'Confirm in Wallet...'
                : swapStatus === 'processing'
                ? 'Processing Transaction...'
                : swapStatus === 'success'
                ? 'Swap Successful!'
                : swapStatus === 'error'
                ? 'Swap Failed'
                : !fromAmount
                ? 'Enter Amount'
                : fromToken && toToken
                ? `Swap ${fromToken.symbol} for ${toToken.symbol}`
                : 'Select Tokens'}
            </button>

            {swapStatus === 'success' && (txHash || sendTxHash) && (
              <div className='transaction-success'>
                <span>✅ Transaction Successful!</span>
                <a href={`https://snowtrace.io/tx/${txHash || sendTxHash}`} target='_blank' rel='noopener noreferrer' className='tx-link'>
                  View on Snowtrace
                </a>
              </div>
            )}

            {swapStatus === 'error' && (
              <div className='transaction-error'>
                <span>❌ Transaction Failed. Please try again.</span>
              </div>
            )}
          </div>

          <div className='swap-info'>
            <FaInfoCircle />
            <span>Powered by KyberSwap Aggregator for best rates</span>
          </div>
        </div>

        <div className='swap-stats glass-card'>
          <h3>Market Info</h3>
          <div className='market-stats'>
            <div className='market-stat'>
              <span className='stat-label'>VELAI Price</span>
              <span className='stat-value'>${marketData.priceUsd ? marketData.priceUsd.toFixed(4) : '0.0000'}</span>
            </div>
            <div className='market-stat'>
              <span className='stat-label'>24h Volume</span>
              <span className='stat-value'>{formatMarketValue(marketData.volume24h)}</span>
            </div>
            <div className='market-stat'>
              <span className='stat-label'>Liquidity</span>
              <span className='stat-value'>{formatMarketValue(marketData.liquidity)}</span>
            </div>
            <div className='market-stat'>
              <span className='stat-label'>Market Cap</span>
              <span className='stat-value'>{formatMarketValue(marketData.marketCap)}</span>
            </div>
          </div>
        </div>
        </motion.div>
      </div>

      {/* Token Selection Modal */}
      <AnimatePresence>
        {showTokenModal && (
          <motion.div
            className='token-modal-overlay'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeTokenModal}
          >
            <motion.div
              className='token-modal'
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className='token-modal-header'>
                <h3>Select a Token</h3>
                <button className='modal-close-btn' onClick={closeTokenModal}>
                  <FaTimes />
                </button>
              </div>

              <div className='token-search-section'>
                <div className='token-search-wrapper'>
                  <FaSearch className='search-icon' />
                  <input
                    type='text'
                    placeholder='Search name or paste address'
                    value={searchQuery || customTokenAddress}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.startsWith('0x') && value.length === 42) {
                        setCustomTokenAddress(value);
                        setSearchQuery('');
                      } else {
                        setSearchQuery(value);
                        setCustomTokenAddress('');
                      }
                    }}
                    className='token-search-input'
                  />
                </div>
              </div>

              <div className='token-list-section'>
                <div className='token-list-header'>
                  <span>Token</span>
                  <span>Balance</span>
                </div>
                
                <div className='token-list'>
                  {Object.values(tokens)
                    .filter((token) => {
                      if (searchQuery) {
                        return token.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
                               token.name.toLowerCase().includes(searchQuery.toLowerCase());
                      }
                      return true;
                    })
                    .map((token) => (
                      <div
                        key={token.symbol}
                        className='token-list-item'
                        onClick={() => selectToken(token.symbol)}
                      >
                        <div className='token-info'>
                          <img src={token.image} alt={token.symbol} className='token-list-image' />
                          <div className='token-details'>
                            <span className='token-list-symbol'>{token.symbol}</span>
                            <span className='token-list-name'>{token.name}</span>
                          </div>
                        </div>
                        <div className='token-balance'>
                          <span>{getTokenBalance(token).toFixed(4)}</span>
                        </div>
                      </div>
                    ))}
                </div>

                {customTokenAddress && (
                  <div className='custom-token-section'>
                    <div className='custom-token-notice'>
                      <FaInfoCircle />
                      <span>Custom token import coming soon</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Swap;
